import {
  createContext, useCallback, useContext, useEffect, useMemo, type ReactNode,
} from 'react'
import { formatScalarMessage, type InterfaceLocale, type LocalizationContext, type LocalizedMessage } from '@solum/experience'

export type LocalizationState = 'loading' | 'ready' | 'refused' | 'error'

export type LocalizationValue = {
  locale: InterfaceLocale
  localeSource: 'user' | 'persona' | 'deployment' | 'requested'
  state: LocalizationState
  message: (key: string, sourceFallback?: string, variables?: Readonly<Record<string, unknown>>) => string
  evidence: (key: string) => LocalizedMessage | null
  retry?: () => void
}

export type LocalizationProviderProps = {
  context?: LocalizationContext | null
  state: LocalizationState
  sourceMessages: Readonly<Record<string, string>>
  retry?: () => void
  children: ReactNode
}

const Localization = createContext<LocalizationValue | null>(null)

export function LocalizationProvider({
  context, state, sourceMessages, retry, children,
}: LocalizationProviderProps) {
  const rows = useMemo(() => new Map(context?.messages.map((row) => [row.key, row]) ?? []), [context])
  const locale = context?.locale ?? 'en'
  const localeSource = context?.localeSource ?? 'deployment'
  const message = useCallback((key: string, sourceFallback?: string, variables?: Readonly<Record<string, unknown>>) => {
    const row = rows.get(key)
    const pattern = row && !row.missing ? row.value : sourceMessages[key] ?? sourceFallback ?? ''
    return formatScalarMessage(pattern, variables)
  }, [rows, sourceMessages])
  const evidence = useCallback((key: string) => rows.get(key) ?? null, [rows])

  useEffect(() => {
    document.documentElement.lang = locale
  }, [locale])

  const value = useMemo<LocalizationValue>(() => ({
    locale, localeSource, state, message, evidence, retry,
  }), [locale, localeSource, state, message, evidence, retry])
  return <Localization.Provider value={value}>{children}</Localization.Provider>
}

export function useLocalization(): LocalizationValue {
  const value = useContext(Localization)
  if (!value) throw new Error('useLocalization must be used inside LocalizationProvider')
  return value
}

/** Shared components may render in isolated tests or non-host surfaces. */
export function useOptionalLocalization(): LocalizationValue | null {
  return useContext(Localization)
}

export function useMessage(
  key: string, sourceFallback?: string, variables?: Readonly<Record<string, unknown>>,
): string {
  return useLocalization().message(key, sourceFallback, variables)
}
